import React, { useState, useEffect } from 'react'
import { Card, Form, Button, Alert, Container, Row, Col, Modal, Spinner } from 'react-bootstrap'
import d_log from 'loglevel'
import api_utils from '../lib/api_utils'

const CHAT_HISTORY_KEY = 'dsrag_chat_history'
const LAST_RESPONSE_KEY = 'dsrag_last_response'

function Chat() {
  const [messages, setMessages] = useState([])
  const [userInput, setUserInput] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [lastResponse, setLastResponse] = useState(null)
  const [showJsonModal, setShowJsonModal] = useState(false)
  const [showFlagModal, setShowFlagModal] = useState(false)
  const [flaggingMessage, setFlaggingMessage] = useState(null)
  const [flagNotes, setFlagNotes] = useState('')
  const [flagging, setFlagging] = useState(false)
  const [favoriting, setFavoriting] = useState(false)

  // Load chat history from localStorage on component mount
  useEffect(() => {
    try {
      const savedMessages = localStorage.getItem(CHAT_HISTORY_KEY)
      const savedLastResponse = localStorage.getItem(LAST_RESPONSE_KEY)

      if (savedMessages) {
        const parsedMessages = JSON.parse(savedMessages)
        setMessages(parsedMessages)
        d_log.debug('Loaded chat history from localStorage:', parsedMessages.length, 'messages')
      }

      if (savedLastResponse) {
        const parsedLastResponse = JSON.parse(savedLastResponse)
        setLastResponse(parsedLastResponse)
        d_log.debug('Loaded last response from localStorage')
      }
    } catch (err) {
      d_log.error('Failed to load chat history from localStorage:', err)
      // Clear corrupted data
      localStorage.removeItem(CHAT_HISTORY_KEY)
      localStorage.removeItem(LAST_RESPONSE_KEY)
    }
  }, [])

  // Save to localStorage whenever messages change
  useEffect(() => {
    try {
      if (messages.length > 0) {
        localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(messages))
        d_log.debug('Saved chat history to localStorage:', messages.length, 'messages')
      } else {
        localStorage.removeItem(CHAT_HISTORY_KEY)
        d_log.debug('Removed empty chat history from localStorage')
      }
    } catch (err) {
      d_log.error('Failed to save chat history to localStorage:', err)
    }
  }, [messages])

  // Save to localStorage whenever lastResponse changes
  useEffect(() => {
    try {
      if (lastResponse) {
        localStorage.setItem(LAST_RESPONSE_KEY, JSON.stringify(lastResponse))
        d_log.debug('Saved last response to localStorage')
      } else {
        localStorage.removeItem(LAST_RESPONSE_KEY)
        d_log.debug('Removed last response from localStorage')
      }
    } catch (err) {
      d_log.error('Failed to save last response to localStorage:', err)
    }
  }, [lastResponse])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!userInput.trim()) return

    const question = userInput.trim()
    setUserInput('')
    setLoading(true)
    setError(null)

    // Add user message to chat
    const userMessage = {
      type: 'user',
      content: question,
      timestamp: new Date().toLocaleTimeString()
    }
    setMessages(prev => [...prev, userMessage])

    try {
      d_log.debug('Sending question to RAG query endpoint:', question)

      const ragQueryUrl = `${import.meta.env.VITE_SERVICE_URL}/infer/rag-query`
      const headers = api_utils.form_auth_headers()

      // Make POST request with question in JSON format
      const response = await fetch(ragQueryUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({ question: question })
      })

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }

      const responseData = await response.json()
      d_log.debug('RAG query response:', responseData)

      setLastResponse(responseData)

      // Add AI response to chat
      const aiMessage = {
        type: 'ai',
        content: responseData.answer || 'No answer provided in response',
        timestamp: new Date().toLocaleTimeString(),
        hasJsonResponse: true
      }
      setMessages(prev => [...prev, aiMessage])

    } catch (err) {
      d_log.error('Failed to get response from RAG query:', err)
      setError(`Failed to get response: ${err.message}`)

      // Add error message to chat
      const errorMessage = {
        type: 'error',
        content: `Error: ${err.message}`,
        timestamp: new Date().toLocaleTimeString()
      }
      setMessages(prev => [...prev, errorMessage])
    } finally {
      setLoading(false)
    }
  }

  const handleShowJson = () => {
    setShowJsonModal(true)
  }

  const handleCloseJson = () => {
    setShowJsonModal(false)
  }

  const handleFlagMessage = (messageIndex) => {
    const message = messages[messageIndex]
    if (message && message.type === 'user') {
      setFlaggingMessage({ messageIndex, message })
      setFlagNotes('')
      setShowFlagModal(true)
      d_log.debug('Opening flag modal for message:', message.content)
    }
  }

  const handleCloseFlagModal = () => {
    setShowFlagModal(false)
    setFlaggingMessage(null)
    setFlagNotes('')
  }

  const handleSubmitFlag = async () => {
    if (!flaggingMessage) return

    setFlagging(true)
    try {
      const userMessage = flaggingMessage.message
      // Find the corresponding AI response (next message after user message)
      const aiResponse = messages[flaggingMessage.messageIndex + 1]
      const answer = aiResponse?.type === 'ai' ? aiResponse.content : ''

      d_log.debug('Flagging message:', {
        question: userMessage.content,
        answer: answer,
        notes: flagNotes
      })

      const flagUrl = `${import.meta.env.VITE_SERVICE_URL}/infer/rag-flag`
      const headers = api_utils.form_auth_headers()

      await fetch(flagUrl, {
        method: 'POST',
        headers: headers,
        body: JSON.stringify({
          question: userMessage.content,
          answer: answer,
          notes: flagNotes
        })
      })

      d_log.debug('Message flagged successfully')

      // Update the message to show it's been flagged
      const updatedMessages = [...messages]
      updatedMessages[flaggingMessage.messageIndex] = {
        ...userMessage,
        flagged: true
      }
      setMessages(updatedMessages)

      handleCloseFlagModal()
    } catch (err) {
      d_log.error('Failed to flag message:', err)
      setError(`Failed to flag message: ${err.message}`)
    } finally {
      setFlagging(false)
    }
  }

  const handleFavoriteMessage = async (messageIndex) => {
    const message = messages[messageIndex]
    if (message && message.type === 'user' && !message.favorited) {
      setFavoriting(true)
      try {
        // Find the corresponding AI response (next message after user message)
        const aiResponse = messages[messageIndex + 1]
        const answer = aiResponse?.type === 'ai' ? aiResponse.content : ''

        d_log.debug('Favoriting message:', {
          question: message.content,
          answer: answer
        })

        const favoriteUrl = `${import.meta.env.VITE_SERVICE_URL}/infer/rag-favorites`
        const headers = api_utils.form_auth_headers()

        await fetch(favoriteUrl, {
          method: 'POST',
          headers: headers,
          body: JSON.stringify({
            question: message.content,
            answer: answer
          })
        })

        d_log.debug('Message favorited successfully')

        // Update the message to show it's been favorited
        const updatedMessages = [...messages]
        updatedMessages[messageIndex] = {
          ...message,
          favorited: true
        }
        setMessages(updatedMessages)

      } catch (err) {
        d_log.error('Failed to favorite message:', err)
        setError(`Failed to favorite message: ${err.message}`)
      } finally {
        setFavoriting(false)
      }
    }
  }

  const handleClearChat = () => {
    d_log.debug('User requested to clear chat history')
    setMessages([])
    setLastResponse(null)
    setError(null)

    // Clear localStorage
    try {
      localStorage.removeItem(CHAT_HISTORY_KEY)
      localStorage.removeItem(LAST_RESPONSE_KEY)
      d_log.debug('Cleared chat history from localStorage')
    } catch (err) {
      d_log.error('Failed to clear chat history from localStorage:', err)
    }
  }

  return (
    <Container>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h1>AI Chat</h1>
          <p className="text-muted mb-0">Ask questions and get AI-powered responses from your documents</p>
        </div>
        <Button
          variant="outline-danger"
          onClick={handleClearChat}
          disabled={loading || messages.length === 0}
        >
          🗑️ Clear Chat
        </Button>
      </div>

      {error && (
        <Alert variant="danger" className="mt-3">
          {error}
        </Alert>
      )}

      {/* Chat Messages */}
      <Card className="mt-4" style={{ height: '400px' }}>
        <Card.Header>
          <h5>Conversation</h5>
        </Card.Header>
        <Card.Body style={{ overflowY: 'auto', padding: '1rem' }}>
          {messages.length === 0 ? (
            <div className="text-center text-muted">
              <p>Start a conversation by asking a question below!</p>
            </div>
          ) : (
            messages.map((message, index) => (
              <div key={index} className={`mb-3 ${message.type === 'user' ? 'text-end' : 'text-start'}`}>
                <div className="d-flex align-items-start" style={{ flexDirection: message.type === 'user' ? 'row-reverse' : 'row' }}>
                  <div
                    className={`d-inline-block p-2 rounded ${
                      message.type === 'user'
                        ? 'bg-primary text-white'
                        : message.type === 'error'
                        ? 'bg-danger text-white'
                        : 'bg-light border'
                    }`}
                    style={{ maxWidth: '70%' }}
                  >
                    <div>{message.content}</div>
                    <small className="opacity-75">{message.timestamp}</small>
                    {message.hasJsonResponse && (
                      <div className="mt-2">
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 text-white text-decoration-underline"
                          onClick={handleShowJson}
                        >
                          View full response
                        </Button>
                      </div>
                    )}
                  </div>

                  {message.type === 'user' && (
                    <div className="ms-2 me-0 d-flex flex-column" style={{ marginLeft: message.type === 'user' ? '8px' : '0', marginRight: message.type === 'user' ? '0' : '8px' }}>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1 mb-1"
                        onClick={() => handleFavoriteMessage(index)}
                        disabled={message.favorited || favoriting}
                        title={message.favorited ? "Message already favorited" : "Add to favorites"}
                      >
                        <span style={{ color: message.favorited ? '#ffc107' : '#28a745', fontSize: '14px' }}>
                          👍
                        </span>
                      </Button>
                      <Button
                        variant="link"
                        size="sm"
                        className="p-1"
                        onClick={() => handleFlagMessage(index)}
                        disabled={message.flagged}
                        title={message.flagged ? "Message already flagged" : "Flag this message"}
                      >
                        <span style={{ color: message.flagged ? '#6c757d' : '#dc3545', fontSize: '14px' }}>
                          🚩
                        </span>
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
          {loading && (
            <div className="text-center">
              <Spinner animation="border" size="sm" className="me-2" />
              <span className="text-muted">AI is thinking...</span>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Chat Input */}
      <Card className="mt-3">
        <Card.Body>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md={10}>
                <Form.Control
                  type="text"
                  placeholder="Ask a question..."
                  value={userInput}
                  onChange={(e) => setUserInput(e.target.value)}
                  disabled={loading}
                />
              </Col>
              <Col md={2}>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading || !userInput.trim()}
                  className="w-100"
                >
                  {loading ? (
                    <Spinner animation="border" size="sm" />
                  ) : (
                    'Send'
                  )}
                </Button>
              </Col>
            </Row>
          </Form>
        </Card.Body>
      </Card>

      {/* JSON Response Modal */}
      <Modal show={showJsonModal} onHide={handleCloseJson} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Full JSON Response</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {lastResponse ? (
            <pre className="bg-light p-3 rounded" style={{ maxHeight: '400px', overflowY: 'auto' }}>
              {JSON.stringify(lastResponse, null, 2)}
            </pre>
          ) : (
            <p className="text-muted">No response data available</p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseJson}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Flag Message Modal */}
      <Modal show={showFlagModal} onHide={handleCloseFlagModal}>
        <Modal.Header closeButton>
          <Modal.Title>
            <span className="text-danger">🚩</span> Flag Message
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {flaggingMessage && (
            <>
              <div className="mb-3">
                <h6>Question to be flagged:</h6>
                <div className="p-2 bg-light rounded border">
                  {flaggingMessage.message.content}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="flagNotes" className="form-label">
                  <strong>Notes (required):</strong>
                </label>
                <textarea
                  id="flagNotes"
                  className="form-control"
                  rows={4}
                  placeholder="Please provide details about why this message is being flagged..."
                  value={flagNotes}
                  onChange={(e) => setFlagNotes(e.target.value)}
                  disabled={flagging}
                />
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseFlagModal} disabled={flagging}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleSubmitFlag}
            disabled={flagging || !flagNotes.trim()}
          >
            {flagging ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Flagging...
              </>
            ) : (
              <>
                🚩 Flag Message
              </>
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default Chat